import React, { useState, useEffect, useCallback } from "react";
import { Table, Button, Space, message, Tag, Modal } from "antd";
import { useNavigate } from "react-router-dom";
import { DownloadOutlined } from "@ant-design/icons";
import { saveAs } from "file-saver";
import api from "../api";
import FilterToolbar from "../components/FilterToolbar";

const AttendanceHistoryPage = () => {
  const [history, setHistory] = useState([]);
  const [allSessions, setAllSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [classOptions, setClassOptions] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState(undefined);
  const navigate = useNavigate();

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/attendance/sessions");
      if (Array.isArray(response.data)) {
        // Filter: Show ENDED sessions more than 10 minutes ago
        const now = Date.now();
        const tenMinutes = 10 * 60 * 1000;
        
        const filtered = response.data.filter(session => {
            if (String(session.status).toUpperCase() !== 'ENDED') return false;
            if (!session.endedAt) return false;
            const endedTime = new Date(session.endedAt).getTime();
            return (now - endedTime) > tenMinutes;
        });
        
        // 按开始时间降序排序（最新的在前面）
        filtered.sort((a, b) => {
          const timeA = a.startedAt ? new Date(a.startedAt).getTime() : 0;
          const timeB = b.startedAt ? new Date(b.startedAt).getTime() : 0;
          return timeB - timeA;
        });
        
        setAllSessions(filtered);
        setHistory(filtered);
      } else {
        setAllSessions([]);
        setHistory([]);
        console.error("Expected array from /attendance/sessions but got:", response.data);
      }
    } catch (error) {
      message.error("获取签到历史失败");
      setAllSessions([]);
      setHistory([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchClasses = useCallback(async () => {
    try {
      const res = await api.get("/classes/manageable");
      const opts = (res.data || []).map(c => ({ label: c.name, value: c.id }));
      setClassOptions([{ label: "所有班级", value: 0 }, ...opts]);
    } catch {
      setClassOptions([]);
    }
  }, []);

  const applyFilters = (sessions, date, classId) => {
    let data = sessions || allSessions;
    if (date) {
      const dayStr = date.format("YYYY-MM-DD");
      data = data.filter(s => s.endedAt && new Date(s.endedAt).toISOString().slice(0,10) === dayStr);
    }
    if (classId !== undefined) {
      if (classId === 0) {
        // 显示“所有班级”记录
        data = data.filter(s => s.classId === 0);
      } else {
        data = data.filter(s => s.classId === classId);
      }
    }
    setHistory(data);
  };

  const onDateChange = (value) => {
    setSelectedDate(value);
    applyFilters(allSessions, value, selectedClassId);
  };

  const onClassChange = (value) => {
    setSelectedClassId(value);
    applyFilters(allSessions, selectedDate, value);
  };

  const resetFilters = () => {
    setSelectedDate(null);
    setSelectedClassId(undefined);
    setHistory(allSessions);
  };

  const handleDeleteSession = async (id) => {
    try {
      await api.delete(`/attendance/sessions/${id}`);
      message.success('签到记录已删除');
      fetchHistory();
    } catch (error) {
      message.error('删除失败，请重试');
    }
  };

  const handleBatchExport = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请至少选择一条记录');
      return;
    }
    
    try {
      message.loading({ content: '正在生成导出文件...', key: 'exporting' });
      const response = await api.post('/attendance/export', selectedRowKeys, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, `签到记录导出_${new Date().toISOString().slice(0,10)}.xlsx`);
      message.success({ content: '导出成功', key: 'exporting' });
      setSelectedRowKeys([]);
    } catch (error) {
      console.error(error);
      message.error({ content: '导出失败', key: 'exporting' });
    }
  };

  useEffect(() => {
    fetchHistory();
    fetchClasses();
  }, [fetchHistory, fetchClasses]);

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
    getCheckboxProps: (record) => ({
      'aria-label': `选择 ${record.className || ''} 的签到记录`,
    }),
  };

  const columns = [
    {
      title: "班级",
      dataIndex: "className",
      key: "className",
    },
    {
      title: "发起人",
      dataIndex: "teacherName",
      key: "teacherName",
    },
    {
      title: "开始时间",
      dataIndex: "startedAt",
      key: "startedAt",
      render: (text) => new Date(text).toLocaleString(),
    },
    {
        title: "结束时间",
        dataIndex: "endedAt",
        key: "endedAt",
        render: (text) => text ? new Date(text).toLocaleString() : "-",
      },
    {
      title: "状态",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const s = String(status || '').toUpperCase();
        let color = 'blue';
        let text = status;
        
        if (s === 'ENDED') {
            color = 'green';
            text = '已结束';
        } else if (s === 'ACTIVE') {
            color = 'processing';
            text = '进行中';
        }
        
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: "操作",
      key: "action",
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="primary"
            onClick={() => navigate(`/attendance/history/${record.id}`)}
          >
            查看详情
          </Button>
          <Button type="link" danger onClick={() => Modal.confirm({
              title: '确认删除',
              content: '删除签到将同时清除所有相关的签到记录，是否继续？',
              onOk: () => handleDeleteSession(record.id)
          })}>删除</Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>历史签到</h2>
        <FilterToolbar
          dateMode="single"
          dateValue={selectedDate}
          onDateChange={onDateChange}
          classOptions={classOptions}
          classValue={selectedClassId}
          onClassChange={onClassChange}
          onReset={resetFilters}
        />
        <Button 
          type="primary" 
          icon={<DownloadOutlined />} 
          onClick={handleBatchExport}
          disabled={selectedRowKeys.length === 0}
        >
          批量导出
        </Button>
      </div>
      <Table
        rowSelection={rowSelection}
        columns={columns}
        dataSource={history}
        loading={loading}
        rowKey="id"
        scroll={{ x: 800 }}
        pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: [10, 20, 50, 100], showTotal: (total) => `共 ${total} 条` }}
      />
    </div>
  );
};

export default AttendanceHistoryPage;
